/**
 * ZruseniModal — cancelling a reservation, with the reason (optional, at most 100 characters: the API's limit). It replaces the plain
 * confirmation: the reason and the moment are stored with the cancellation (`zruseno_duvod` / `zruseno_at`) and shown in the list of the
 * cancelled ones, so "why is this gone" has an answer. Nothing can dismiss it while the request is in flight.
 */

import { useState } from 'react';
import { Button } from '@/components/Button';
import { Field, Fields } from '@/components/Field';
import { Modal, DialogCrown } from '@/components/Modal';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import type { ApiError } from '@/lib/api';
import { useCancelRezervaceMutation, type Rezervace } from './api';
import { formatTermin } from './rezervace-helpers';

export const DUVOD_MAX = 100;

interface ZruseniModalProps {
  rezervace: Rezervace;
  /** What the dialog titles the car with (plate or VIN). */
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ZruseniModal({ rezervace, title, open, onOpenChange }: ZruseniModalProps) {
  const cancel = useCancelRezervaceMutation();
  const [duvod, setDuvod] = useState('');
  const busy = cancel.isPending;
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };
  const submit = () => {
    if (busy) return;
    cancel.mutate(
      { id: rezervace.id, duvod: duvod.trim() },
      {
        onSuccess: () => {
          toast.ok('Rezervace zrušena.');
          onOpenChange(false);
        },
        onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se zrušit rezervaci.'}`),
      },
    );
  };

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={520}>
      <DialogCrown
        eyebrow="Zrušení"
        title={
          <>
            Zrušit rezervaci <em>{title}</em>?
          </>
        }
        sub={`${formatTermin(rezervace.od, rezervace.do)}. Vůz se v tomto termínu uvolní, záznam zůstane v historii.`}
      />
      <Modal.Body className="px-8 py-5">
        <form
          id="zruseni-form"
          data-testid="zruseni-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Fields cols={1}>
            <Field label="Důvod zrušení" optional="volitelné" hint={`Uloží se se zrušením a ukáže se v seznamu zrušených (nejvýše ${DUVOD_MAX} znaků).`}>
              <TextInput value={duvod} maxLength={DUVOD_MAX} disabled={busy} data-testid="zruseni-duvod" onChange={(e) => setDuvod(e.currentTarget.value)} />
            </Field>
          </Fields>
        </form>
      </Modal.Body>
      <Modal.Foot
        actions={
          <>
            <Button variant="secondary" onClick={() => guarded(false)} disabled={busy}>
              Ponechat rezervaci
            </Button>
            <Button variant="ink" type="submit" form="zruseni-form" disabled={busy} data-testid="zruseni-potvrdit">
              Zrušit rezervaci
            </Button>
          </>
        }
      />
    </Modal>
  );
}
